import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import $ from "jquery";
import { Diamond, Package, Search, Sparkles } from "lucide-react";
import ApiURl from "../../controllers/Api";

const CARD = "rounded-2xl border border-[#eadfd6] bg-[#fffaf5] shadow-sm";

function StockUpdateModal({ isOpen, onClose, product, onUpdate }) {
  const [stockValue, setStockValue] = useState("");
  const [updating, setUpdating] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !product) return;
    setStockValue(String(product.stock_quantity || 0));
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [isOpen, product]);

  if (!isOpen || !product) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    const newStock = Number.parseInt(stockValue, 10);
    if (Number.isNaN(newStock) || newStock < 0) {
      alert("Please enter a valid stock quantity");
      return;
    }

    setUpdating(true);
    try {
      const formData = new FormData();
      formData.append("product_id", product.product_id);
      formData.append("stock_quantity", newStock);

      const response = await $.ajax({
        url: `${ApiURl}updateProductimg.php`,
        type: "POST",
        data: formData,
        processData: false,
        contentType: false,
        dataType: "json",
      });

      if (response?.success) {
        onUpdate(product.product_id, newStock);
        onClose();
      } else {
        alert(response?.message || "Failed to update stock");
      }
    } catch (error) {
      console.error("Stock update error:", error);
      alert("Error updating stock");
    } finally {
      setUpdating(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <form
        onSubmit={handleSubmit}
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold text-[#2B0000]">Update Stock</h3>
            <p className="mt-1 text-sm text-slate-500">{product.name}</p>
          </div>
          <button type="button" onClick={onClose} className="text-2xl leading-none text-slate-400">
            x
          </button>
        </div>

        <label className="block text-sm font-semibold text-slate-700">
          Stock Quantity
          <input
            ref={inputRef}
            type="number"
            min="0"
            value={stockValue}
            onChange={(event) => setStockValue(event.target.value)}
            className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-[#b29063]"
          />
        </label>

        <div className="mt-5 flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 rounded-xl border px-4 py-3 font-semibold">
            Cancel
          </button>
          <button
            type="submit"
            disabled={updating}
            className="flex-1 rounded-xl bg-[#2B0000] px-4 py-3 font-semibold text-white disabled:opacity-50"
          >
            {updating ? "Updating..." : "Update"}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}

export default function StockManagement() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [filterType, setFilterType] = useState("lowest");
  const [filterLimit, setFilterLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("stock_asc");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${ApiURl}getProducts.php`);
      const data = await response.json();
      setProducts(data?.success ? data.products || [] : []);
    } catch (error) {
      console.error("Error fetching products:", error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const categories = useMemo(
    () => [...new Set(products.map((product) => product.category_name).filter(Boolean))],
    [products]
  );

  const stockStats = useMemo(() => {
    const total = products.length;
    const out = products.filter((p) => Number(p.stock_quantity || 0) <= 0).length;
    const critical = products.filter((p) => Number(p.stock_quantity || 0) > 0 && Number(p.stock_quantity || 0) < 5).length;
    const low = products.filter((p) => Number(p.stock_quantity || 0) > 0 && Number(p.stock_quantity || 0) < 10).length;
    const healthy = products.filter((p) => Number(p.stock_quantity || 0) >= 20).length;
    return { total, healthy, low, critical, out };
  }, [products]);

  const filteredProducts = useMemo(() => {
    let next = [...products];

    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      next = next.filter(
        (product) =>
          product.name?.toLowerCase().includes(query) ||
          String(product.product_id || "").includes(query)
      );
    }

    if (categoryFilter !== "all") {
      next = next.filter((product) => product.category_name === categoryFilter);
    }

    if (filterType === "critical") next = next.filter((p) => Number(p.stock_quantity || 0) > 0 && Number(p.stock_quantity || 0) < 5);
    if (filterType === "low") next = next.filter((p) => Number(p.stock_quantity || 0) > 0 && Number(p.stock_quantity || 0) < 10);
    if (filterType === "out") next = next.filter((p) => Number(p.stock_quantity || 0) <= 0);
    if (filterType === "healthy") next = next.filter((p) => Number(p.stock_quantity || 0) >= 20);
    if (filterType === "lowest") next = next.filter((p) => Number(p.stock_quantity || 0) > 0);

    if (sortBy === "stock_asc") next.sort((a, b) => Number(a.stock_quantity || 0) - Number(b.stock_quantity || 0));
    if (sortBy === "stock_desc") next.sort((a, b) => Number(b.stock_quantity || 0) - Number(a.stock_quantity || 0));
    if (sortBy === "name_asc") next.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
    if (sortBy === "name_desc") next.sort((a, b) => String(b.name || "").localeCompare(String(a.name || "")));

    return filterType !== "all" ? next.slice(0, filterLimit) : next;
  }, [products, searchTerm, categoryFilter, filterType, filterLimit, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / itemsPerPage));
  const visibleProducts =
    filterType === "all"
      ? filteredProducts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
      : filteredProducts;

  const updateStock = (productId, stock) => {
    setProducts((prev) =>
      prev.map((product) =>
        String(product.product_id) === String(productId)
          ? { ...product, stock_quantity: stock }
          : product
      )
    );
  };

  const resetFilters = () => {
    setFilterType("lowest");
    setFilterLimit(10);
    setSearchTerm("");
    setCategoryFilter("all");
    setSortBy("stock_asc");
    setCurrentPage(1);
  };

  const getStockLabel = (stock) => {
    const qty = Number(stock || 0);
    if (qty <= 0) return "Out of Stock";
    if (qty < 5) return "Critical";
    if (qty < 10) return "Low";
    return "In Stock";
  };

  return (
    <main className="min-h-screen bg-[#f8efe8] p-4 sm:p-6">
      <StockUpdateModal
        isOpen={Boolean(selectedProduct)}
        product={selectedProduct}
        onClose={() => setSelectedProduct(null)}
        onUpdate={updateStock}
      />

      <section className={`${CARD} p-5 sm:p-6`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#d59432]">Inventory Control</p>
            <h1 className="mt-2 font-serif text-4xl text-[#2B0000]">Stock Management</h1>
            <p className="mt-2 text-sm text-[#8d817a]">Search, filter and update product stock from one place.</p>
          </div>
          <div className="flex gap-3">
            <button onClick={fetchProducts} className="rounded-xl border border-[#eadfd6] bg-white px-4 py-2 font-semibold text-[#2B0000]">
              Refresh
            </button>
            <button onClick={resetFilters} className="rounded-xl bg-[#2B0000] px-4 py-2 font-semibold text-white">
              Reset Filters
            </button>
          </div>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {[
            ["Total Products", stockStats.total],
            ["Healthy Stock", stockStats.healthy],
            ["Low Stock", stockStats.low],
            ["Critical", stockStats.critical],
            ["Out of Stock", stockStats.out],
          ].map(([label, value]) => (
            <article key={label} className="rounded-xl border border-[#eadfd6] bg-white p-4">
              <p className="text-xs font-semibold text-[#8d817a]">{label}</p>
              <strong className="mt-1 block text-2xl text-[#2B0000]">{value}</strong>
            </article>
          ))}
        </div>

        <div className="mt-6 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <select value={filterType} onChange={(e) => { setFilterType(e.target.value); setCurrentPage(1); }} className="rounded-xl border border-[#eadfd6] bg-white px-3 py-3">
            <option value="lowest">Lowest Stock</option>
            <option value="critical">Critical Stock (&lt;5)</option>
            <option value="low">Low Stock (&lt;10)</option>
            <option value="out">Out of Stock</option>
            <option value="healthy">Healthy Stock</option>
            <option value="all">All Products</option>
          </select>

          {filterType !== "all" && (
            <select value={filterLimit} onChange={(e) => setFilterLimit(Number(e.target.value))} className="rounded-xl border border-[#eadfd6] bg-white px-3 py-3">
              <option value={10}>10 Products</option>
              <option value={20}>20 Products</option>
              <option value={50}>50 Products</option>
              <option value={100}>100 Products</option>
            </select>
          )}

          <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }} className="rounded-xl border border-[#eadfd6] bg-white px-3 py-3">
            <option value="all">All Categories</option>
            {categories.map((category) => <option key={category}>{category}</option>)}
          </select>

          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="rounded-xl border border-[#eadfd6] bg-white px-3 py-3">
            <option value="stock_asc">Stock: Low to High</option>
            <option value="stock_desc">Stock: High to Low</option>
            <option value="name_asc">Name: A to Z</option>
            <option value="name_desc">Name: Z to A</option>
          </select>

          <label className="flex items-center gap-2 rounded-xl border border-[#eadfd6] bg-white px-3 py-3">
            <Search className="h-5 w-5 text-[#8d817a]" />
            <input value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} placeholder="Search product" className="w-full bg-transparent outline-none" />
          </label>
        </div>

        <div className="mt-6 overflow-x-auto rounded-2xl border border-[#eadfd6] bg-white">
          {loading ? (
            <div className="grid min-h-[240px] place-items-center text-[#8d817a]">
              <div className="text-center">
                <Sparkles className="mx-auto h-8 w-8 animate-spin text-[#d59432]" />
                <p className="mt-3">Loading products...</p>
              </div>
            </div>
          ) : (
            <table className="min-w-[850px] w-full text-left">
              <thead className="bg-[#f7eee8] text-xs uppercase tracking-[0.12em] text-[#8d817a]">
                <tr>
                  <th className="px-5 py-4">Product</th>
                  <th className="px-5 py-4">ID</th>
                  <th className="px-5 py-4">Category</th>
                  <th className="px-5 py-4">Stock</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#eadfd6]">
                {visibleProducts.map((product) => (
                  <tr key={product.product_id}>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="grid h-11 w-11 place-items-center rounded-xl bg-[#f7eee8] text-[#d59432]">
                          <Diamond className="h-5 w-5" />
                        </div>
                        <span className="font-semibold text-[#2B0000]">{product.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-[#8d817a]">#{product.product_id}</td>
                    <td className="px-5 py-4 text-[#8d817a]">{product.category_name || "Uncategorized"}</td>
                    <td className="px-5 py-4 text-xl font-bold text-[#2B0000]">{product.stock_quantity}</td>
                    <td className="px-5 py-4">
                      <span className="rounded-full bg-[#f7eee8] px-3 py-1 text-xs font-bold text-[#2B0000]">
                        {getStockLabel(product.stock_quantity)}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <button onClick={() => setSelectedProduct(product)} className="rounded-lg bg-[#2B0000] px-4 py-2 text-sm font-semibold text-white">
                        Update
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {!loading && !visibleProducts.length && (
          <div className="py-10 text-center text-[#8d817a]">
            <Package className="mx-auto h-8 w-8" />
            <p className="mt-2">No products match these filters.</p>
          </div>
        )}

        {filterType === "all" && totalPages > 1 && (
          <div className="mt-5 flex items-center justify-between text-sm text-[#8d817a]">
            <span>Page {currentPage} of {totalPages}</span>
            <div className="flex gap-2">
              <button disabled={currentPage === 1} onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} className="rounded-lg border px-3 py-2 disabled:opacity-40">
                Previous
              </button>
              <button disabled={currentPage === totalPages} onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))} className="rounded-lg border px-3 py-2 disabled:opacity-40">
                Next
              </button>
            </div>
          </div>
        )}
      </section>
    </main>
  );
}
